import type { Theme } from "@mui/material";
import {
  Tooltip as MuiTooltip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SlaTargetIcon } from "components/icons/dual-tone/SlaTargetIcon";
import { SlaTimerIcon } from "components/icons/dual-tone/SlaTimerIcon";
import SlaDetailsCard from "pages/modules/service-desk/tickets/components/ticket-details/components/ticket-header/SlaDetailsCard";
import { memo } from "react";
import type { SlaTrackingData, SlaTrackingStatus } from "shared";

type SlaType = "response" | "resolution";

interface SlaTrackingBadgeProps {
  trackingData?: SlaTrackingData | null;
  type: SlaType;
  ticketStatus?: string;
}

const formatRemainingTime = (remainingMinutes: number): string => {
  const isNegative = remainingMinutes < 0;
  const prefix = isNegative ? "-" : "";
  const abs = Math.ceil(Math.abs(remainingMinutes));
  if (abs === 0) return "0m";
  const days = Math.floor(abs / 1440);
  const hours = Math.floor((abs % 1440) / 60);
  const mins = abs % 60;
  if (days > 0 && hours > 0) return `${prefix}${days}d ${hours}h`;
  if (days > 0) return `${prefix}${days}d`;
  if (hours > 0 && mins > 0) return `${prefix}${hours}h ${mins}m`;
  if (hours > 0) return `${prefix}${hours}h`;
  return `${prefix}${mins}m`;
};

const getStatusColors = (
  slaStatus: SlaTrackingStatus | undefined,
  t: Theme,
): { strokeColor: string; fillColor: string } => {
  const sys = t.palette.primitive.system;
  switch (slaStatus) {
    case "on_track":
      return {
        strokeColor: sys.green[800] ?? "",
        fillColor: sys.green[100] ?? "",
      };
    case "met":
      return {
        strokeColor: sys.blue[800] ?? "",
        fillColor: sys.blue[200] ?? "",
      };
    case "at_risk":
      return {
        strokeColor: sys.orange[800] ?? "",
        fillColor: sys.orange[100] ?? "",
      };
    case "breached":
      return {
        strokeColor: sys.red[800] ?? "",
        fillColor: sys.red[100] ?? "",
        // strokeColor: sys.orange[800] ?? "",
        // fillColor: sys.orange[100] ?? "",
      };
    case "paused":
      return {
        strokeColor: sys.yellow[800] ?? "",
        fillColor: sys.yellow[100] ?? "",
      };
    case "pending":
      return {
        strokeColor: sys.violet[700] ?? "",
        fillColor: sys.violet[200] ?? "",
      };
    default:
      return {
        strokeColor: sys.green[800] ?? "",
        fillColor: sys.green[100] ?? "",
      };
  }
};

export const SlaTrackingBadge = memo(
  ({ trackingData, type, ticketStatus }: SlaTrackingBadgeProps) => {
    const theme = useTheme();

    if (!trackingData) return <Typography variant="body-small">—</Typography>;

    const isResponse = type === "response";

    const status = isResponse
      ? trackingData.firstResponseStatus
      : trackingData.resolutionStatus;

    if (status === "disabled")
      return <Typography variant="body-small">—</Typography>;

    const targetMinutes = isResponse
      ? trackingData.firstResponseTargetMinutes
      : trackingData.resolutionTargetMinutes;
    const elapsedMinutes = isResponse
      ? trackingData.firstResponseElapsedMinutes
      : trackingData.resolutionElapsedMinutes;

    const remaining =
      targetMinutes != null && elapsedMinutes != null
        ? formatRemainingTime(targetMinutes - elapsedMinutes)
        : "--";

    const colors = getStatusColors(status, theme);
    const Icon = isResponse ? SlaTimerIcon : SlaTargetIcon;

    let displayText = remaining;
    if (status === "met") {
      displayText = "Met";
    } else if (status === "breached") {
      displayText = "Breached";
    } else if (status === "paused") {
      displayText = remaining;
    } else if (status === "pending") {
      displayText = remaining;
    } else if (status === "on_track") {
      displayText = remaining;
    } else if (status === "at_risk") {
      displayText = remaining;
    }

    // const displayText = status === "met" ? "Met" : remaining;

    const isStatusText = status === "met" || status === "breached";

    const badge = (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          padding: "4px 8px",
          borderRadius: "4px",
          border: `1px solid ${theme.palette.neutral["015"]}`,
        }}
        gap="4px"
      >
        <Icon
          primaryColor={colors.strokeColor}
          secondaryColor={colors.fillColor}
          fontSize="small"
        />
        <Typography
          variant="caption-xs"
          sx={{ color: isStatusText ? undefined : colors.strokeColor }}
        >
          {displayText}
        </Typography>
      </Stack>
    );

    return (
      <MuiTooltip
        title={
          <SlaDetailsCard
            trackingData={trackingData}
            showOnly={type}
            ticketStatus={ticketStatus}
          />
        }
        placement="bottom-start"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              padding: 0,
              maxWidth: "none",
            },
          },
        }}
      >
        {badge}
      </MuiTooltip>
    );
  },
);

SlaTrackingBadge.displayName = "SlaTrackingBadge";
